var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React from "react";
import { Accordion, Button, Row, Col } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { Controller, useFormContext, useFieldArray } from "react-hook-form";
import { RecekTextField, RecekOnixItemPicker, RecekSelection } from "../inputs";
import { useProductRelations } from "../../hooks/product/useProductRelations";
import { useOnixOptions } from "../../hooks/title/useOnixOptions";
import { formatRelatedProductLabel } from "../../utils/formatRelatedProductLabel";
import { getFirstAuthorName, getPublicationYear, } from "../../utils/relatedProductDetails";
var ReadOnlyField = function (_a) {
    var labelId = _a.labelId, value = _a.value;
    return (React.createElement(RecekTextField, { labelId: labelId, value: value || "", onChange: function () { }, disabled: true }));
};
// Read-only summary of the chosen related product, so the user can check it
// is the right one (title alone is ambiguous across editions and authors).
var RelatedProductDetails = function (_a) {
    var _b, _c, _d, _e, _f, _g;
    var product = _a.product;
    return (React.createElement(Row, null,
        React.createElement(Col, { xs: 2 },
            React.createElement(ReadOnlyField, { labelId: "ui-br-agency.product.publisher.label", value: (_c = (_b = product === null || product === void 0 ? void 0 : product.title) === null || _b === void 0 ? void 0 : _b.owner) === null || _c === void 0 ? void 0 : _c.name })),
        React.createElement(Col, { xs: 3 },
            React.createElement(ReadOnlyField, { labelId: "ui-br-agency.product.title.label", value: (_f = (_e = (_d = product === null || product === void 0 ? void 0 : product.title) === null || _d === void 0 ? void 0 : _d.names) === null || _e === void 0 ? void 0 : _e[0]) === null || _f === void 0 ? void 0 : _f.name })),
        React.createElement(Col, { xs: 2 },
            React.createElement(ReadOnlyField, { labelId: "ui-br-agency.product.relatedProduct.firstAuthor.label", value: product && getFirstAuthorName(product) })),
        React.createElement(Col, { xs: 2 },
            React.createElement(ReadOnlyField, { labelId: "ui-br-agency.product.relatedProduct.publicationYear.label", value: product && getPublicationYear(product) })),
        React.createElement(Col, { xs: 3 },
            React.createElement(ReadOnlyField, { labelId: "ui-br-agency.product.product.label", value: (_g = product === null || product === void 0 ? void 0 : product.form) === null || _g === void 0 ? void 0 : _g.description }))));
};
/** ONIX List 51 code "Replaces" – preselected for a previous edition. */
var RELATION_TYPE_REPLACES = "03";
/** ONIX List 51 code "Product in same collection" – preselected for a previous part. */
var RELATION_TYPE_SAME_COLLECTION = "30";
export var ProductRelationsSection = function (_a) {
    var open = _a.open, onToggle = _a.onToggle;
    var formatMessage = useIntl().formatMessage;
    var _b = useFormContext(), control = _b.control, setValue = _b.setValue;
    var _c = useOnixOptions(), productRelationTable = _c.productRelationTable, productRelationStatus = _c.productRelationStatus;
    var _d = useFieldArray({
        control: control,
        name: "productsRelations",
    }), productsRelationsFields = _d.fields, appendProductsRelation = _d.append, removeProductsRelation = _d.remove;
    var _e = useProductRelations(), productSearchResults = _e.productSearchResults, selectedRelatedProducts = _e.selectedRelatedProducts, productSearchLoading = _e.productSearchLoading, productSearchTooShort = _e.productSearchTooShort, productNumbersById = _e.productNumbersById, setSelectedRelatedProducts = _e.setSelectedRelatedProducts, searchProducts = _e.searchProducts, clearRelationAtIndex = _e.clearRelationAtIndex;
    var formatProductLabel = function (product) {
        return formatRelatedProductLabel(product, product.id ? productNumbersById[product.id] : undefined);
    };
    var getProductOptions = function (index) {
        // No options make the Selection show its emptyMessage, the length hint.
        if (productSearchTooShort[index]) {
            return [];
        }
        var placeholderOption = {
            value: "",
            label: formatMessage({
                id: "ui-br-agency.product.select.placeholder",
            }),
        };
        var selectedProduct = selectedRelatedProducts[index];
        var selectedOption = selectedProduct
            ? [
                {
                    value: selectedProduct.id || "",
                    label: formatProductLabel(selectedProduct),
                },
            ]
            : [];
        var searchResults = (productSearchResults[index] || [])
            .filter(function (product) { return product.id !== (selectedProduct === null || selectedProduct === void 0 ? void 0 : selectedProduct.id); })
            .map(function (product) { return ({
            value: product.id || "",
            label: formatProductLabel(product),
        }); });
        return __spreadArray(__spreadArray([placeholderOption], selectedOption, true), searchResults, true);
    };
    var handleProductSelection = function (index, value) {
        var _a;
        var selectedProduct = (_a = productSearchResults[index]) === null || _a === void 0 ? void 0 : _a.find(function (p) { return p.id === value; });
        setValue("productsRelations.".concat(index, ".relatedProductId"), value);
        setSelectedRelatedProducts(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[index] = selectedProduct, _a)));
        });
    };
    var handleRemoveRelation = function (index) {
        removeProductsRelation(index);
        clearRelationAtIndex(index);
    };
    var handleAddRelation = function (relationTypeCode) {
        var relationTypes = productRelationTable === null || productRelationTable === void 0 ? void 0 : productRelationTable.onixCodetableItems;
        var preselectedType = relationTypeCode
            ? relationTypes === null || relationTypes === void 0 ? void 0 : relationTypes.find(function (item) { return item.value === relationTypeCode; })
            : undefined;
        appendProductsRelation({
            relationType: preselectedType !== null && preselectedType !== void 0 ? preselectedType : relationTypes === null || relationTypes === void 0 ? void 0 : relationTypes[0],
            relatedProductId: "",
        });
    };
    return (React.createElement(Accordion, { label: formatMessage({
            id: "ui-br-agency.product.accordion.relatedProducts",
        }), open: open, onToggle: onToggle },
        productsRelationsFields.map(function (field, index) { return (React.createElement("div", { key: field.id, style: {
                marginBottom: "20px",
                padding: "10px",
                border: "1px solid #ddd",
            } },
            React.createElement(Row, null,
                React.createElement(Col, { xs: 3 },
                    React.createElement(Controller, { key: "".concat(field.id, "-relationType"), control: control, name: "productsRelations.".concat(index, ".relationType"), render: function (_a) {
                            var _b, _c;
                            var controllerField = _a.field, fieldState = _a.fieldState;
                            return (React.createElement(RecekOnixItemPicker, { labelId: "ui-br-agency.product.relationType.label", dataOptions: (_b = productRelationTable === null || productRelationTable === void 0 ? void 0 : productRelationTable.onixCodetableItems) !== null && _b !== void 0 ? _b : [], codetableStatus: productRelationStatus, value: controllerField.value, onChange: controllerField.onChange, error: (_c = fieldState.error) === null || _c === void 0 ? void 0 : _c.message, required: true }));
                        } })),
                React.createElement(Col, { xs: 7 },
                    React.createElement(Controller, { key: "".concat(field.id, "-relatedProductId"), control: control, name: "productsRelations.".concat(index, ".relatedProductId"), render: function (_a) {
                            var _b;
                            var controllerField = _a.field, fieldState = _a.fieldState;
                            return (React.createElement(RecekSelection, { labelId: "ui-br-agency.product.productNumber.label", asyncFilter: true, value: controllerField.value, loading: productSearchLoading[index] || false, onFilter: function (query) { return searchProducts(index, query); }, onChange: function (value) {
                                    return handleProductSelection(index, value);
                                }, dataOptions: getProductOptions(index), emptyMessage: formatMessage({
                                    id: "ui-br-agency.product.relatedProducts.minQueryLength",
                                }), required: true, error: (_b = fieldState.error) === null || _b === void 0 ? void 0 : _b.message }));
                        } })),
                React.createElement(Col, { xs: 2 },
                    React.createElement(Button, { buttonStyle: "danger", marginBottom0: true, onClick: function () { return handleRemoveRelation(index); } },
                        React.createElement(FormattedMessage, { id: "ui-br-agency.button.remove" })))),
            React.createElement(RelatedProductDetails, { product: selectedRelatedProducts[index] }))); }),
        React.createElement(Button, { buttonStyle: "default", onClick: function () { return handleAddRelation(RELATION_TYPE_REPLACES); } },
            React.createElement(FormattedMessage, { id: "ui-br-agency.product.relatedProduct.addPreviousEdition" })),
        React.createElement(Button, { buttonStyle: "default", onClick: function () { return handleAddRelation(RELATION_TYPE_SAME_COLLECTION); } },
            React.createElement(FormattedMessage, { id: "ui-br-agency.product.relatedProduct.addPreviousPart" })),
        React.createElement(Button, { buttonStyle: "default", onClick: function () { return handleAddRelation(); } },
            React.createElement(FormattedMessage, { id: "ui-br-agency.product.relatedProduct.add" }))));
};
